import {
  ChatMessage,
  Conversation,
  ConversationRequest,
  FrontEndSettings,
  JobStatus,
  MetadataFolder,
  MetadataResponse,
  UseCase,
  UseCasesResponse,
} from "./models";

export async function callConversationApi(
  options: ConversationRequest,
  abortSignal: AbortSignal,
  useCase: string,
  useInternet: boolean,
  excludedMetadata: string[] = [],
  folderNumber: number,
): Promise<Response> {
  const response = await fetch("/api/conversation", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      messages: options.messages,
      conversation_id: options.id,
      use_case: useCase,
      use_internet: useInternet,
      excluded_metadata: excludedMetadata,
      selected_folders: folderNumber,
    }),
    signal: abortSignal,
  });

  if (!response.ok && response.status !== 202) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData.error));
  }

  return response;
}

export async function getAssistantTypeApi() {
  try {
    const response = await fetch("/api/assistanttype", {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status == 403) {
      window.location.href = "/unauthorised";
      return;
    }

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const config = await response.json(); // Parse JSON response
    return config;
  } catch (error) {
    console.error("Failed to fetch configuration:", error);
    return null; // Return null or some default value in case of error
  }
}

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
  try {
    const response = await fetch("/api/history/read", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        conversation_id: convId,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return [];
    }

    const payload = await response.json();
    const messages: ChatMessage[] = [];

    if (payload?.messages) {
      payload.messages.forEach((msg: any) => {
        const message: ChatMessage = {
          id: msg.id,
          role: msg.role,
          date: msg.createdAt,
          content: msg.content,
          intent: msg.intent ?? "",
          feedback: msg.feedback ?? undefined,
        };
        messages.push(message);
      });
    }

    return messages;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return [];
  }
};

export const historyList = async (
  offset = 0,
): Promise<Conversation[] | null> => {
  try {
    const response = await fetch(`/api/history/list?offset=${offset}`, {
      method: "GET",
      credentials: "include",
    });

    const payload = await response.json();

    if (!Array.isArray(payload)) {
      console.error("There was an issue fetching your data.");
      return null;
    }

    const conversations: Conversation[] = payload.map((conv: any) => {
      const conversation: Conversation = {
        id: conv.id,
        title: conv.title,
        date: conv.createdAt,
        updatedAt: conv?.updatedAt,
        useCase: conv?.useCase,
        messages: [],
      };
      return conversation;
    });

    return conversations;
  } catch (err) {
    console.error("There was an issue fetching your data.", err);
    return null;
  }
};

export const historyUpdate = async (
  messages: ChatMessage[],
  convId: string,
  useCase: string,
): Promise<Response> => {
  try {
    const payload = {
      conversation_id: convId,
      messages: messages,
      use_case: useCase,
    };

    const response = await fetch("/api/history/update", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return new Response(null, {
      status: 500,
      statusText: "Internal Server Error",
    });
  }
};

export const historyRename = async (
  convId: string,
  title: string,
): Promise<Response> => {
  try {
    const response = await fetch("/api/history/rename", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        conversation_id: convId,
        title: title,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return new Response(null, {
      status: 500,
      statusText: "Internal Server Error",
    });
  }
};

export const historyDelete = async (convId: string): Promise<Response> => {
  try {
    const response = await fetch("/api/history/delete", {
      method: "DELETE",
      credentials: "include",
      body: JSON.stringify({
        conversation_id: convId,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return new Response(null, {
      status: 500,
      statusText: "Internal Server Error",
    });
  }
};

export const historyDeleteAll = async (): Promise<Response> => {
  try {
    const response = await fetch("api/history/delete_all", {
      method: "DELETE",
      credentials: "include",
      body: JSON.stringify({}),
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (err) {
    console.error("There was an issue fetching your data.");
    return new Response(null, {
      status: 500,
      statusText: "Internal Server Error",
    });
  }
};

export const uploadDocumentsApi = async (
  files: File[],
  conversationId: string,
  useCase: string,
): Promise<void> => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  formData.append("conversation_id", conversationId);
  formData.append("use_case", useCase);

  const response = await fetch("/api/upload", {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData.error));
  }
};

export interface ConversationDocument {
  name: string;
  creation_time: string;
}

export const getConversationDocumentsApi = async (
  conversationId: string,
): Promise<ConversationDocument[]> => {
  const formData = new FormData();
  formData.append("conversation_id", conversationId);

  const response = await fetch("/api/documents_by_conversation_id", {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData.error));
  }
  return response.json();
};

export async function getFrontEndSettings(): Promise<FrontEndSettings> {
  try {
    const response = await fetch("/api/history/frontend_settings", {
      method: "GET",
      credentials: "include",
    });
    if (response.status == 401) {
      throw new Error("Authentication Required");
    }
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const responseJSON = await response.json();
    return responseJSON;
  } catch (error) {
    console.error("Failed to fetch Front End Settings:", error);
    return { CHAT_HISTORY_ENABLED: false, error };
  }
}

export const getMetadataFolders = async (): Promise<MetadataFolder[]> => {
  try {
    const response = await fetch("/api/get_metadata_folders", {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Authentication Required");
      }
      throw new Error("Network response was not ok");
    }

    const data: MetadataResponse = await response.json();
    return data.folders || [];
  } catch (error) {
    console.error("Failed to fetch metadata folders:", error);
    return [];
  }
};

export async function pollJobStatus(
  jobId: string,
  onComplete: (result: JobStatus) => void,
  onError: (error: string) => void,
  intervalMs = 5000,
  maxAttempts = 180,
): Promise<void> {
  let attempts = 0;

  const poll = async () => {
    attempts++;
    try {
      const response = await fetch(`/api/conversation/status/${jobId}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        onError(`Failed to fetch job status: ${response.status}`);
        return;
      }

      const job: JobStatus = await response.json();

      if (job.status === "completed") {
        onComplete(job);
        return;
      }

      if (job.status === "failed") {
        onError(job.error ?? "Job failed");
        return;
      }

      if (attempts >= maxAttempts) {
        onError("Job timed out waiting for completion");
        return;
      }

      setTimeout(poll, intervalMs);
    } catch (err) {
      onError(`Polling error: ${err}`);
    }
  };

  setTimeout(poll, intervalMs);
}

let _useCasesPromise: Promise<UseCase[]> | null = null;

export const getUseCases = async (): Promise<UseCase[]> => {
  if (_useCasesPromise) return _useCasesPromise;

  _useCasesPromise = (async () => {
    try {
      const response: Response = await fetch("/api/get_use_cases", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication Required");
        }
        throw new Error("Network response was not ok");
      }

      const useCases: UseCase[] = await response.json().then((data) => {
        return data.use_cases.map((uc: any) => ({
          id: uc.id,
          name: uc.name,
          description: uc.description,
          requirements: uc.requirements,
          confirmationMessage: uc.confirmation_message,
          icon: uc.icon,
        }));
      });
      return useCases;
    } catch (error) {
      _useCasesPromise = null; // allow retry on failure
      console.error("Failed to fetch use cases:", error);
      return [];
    }
  })();

  return _useCasesPromise;
};
